import { useTranslation } from "react-i18next";
import { Form, Modal, Row } from "react-bootstrap";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { ClaimsByPersonResponseDto } from "../../types";
import TextPill from "../../../../components/TextPill";
import { truncateText } from "../../../../utils/textUtils";
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";
import { ClaimPersonTypeEnum } from "../../types/enums";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import { useState } from "react";
import { AppDispatch, RootState } from "../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "../../../../types/Pagination";
import { HistoricalTableKey } from "../../../../redux/historicalSlice";
import { FilterTableKey, setFilterByOnlyActiveRecords } from "../../../../redux/filtersSlice";

interface ClaimsForPersonModalProps {
    show: boolean;
    claimsForPerson: Pagination<ClaimsByPersonResponseDto>;
    onClose: () => void;
    paginationType: HistoricalTableKey;
}

export default function ClaimsForPersonModal({
    show,
    claimsForPerson,
    onClose,
    paginationType
}: ClaimsForPersonModalProps) {
    const { t } = useTranslation(["claim"]);

    const dispatch = useDispatch<AppDispatch>();
    const showOnlyActives = useSelector(
        (state: RootState) =>
            state.filters[paginationType as FilterTableKey].filterByOnlyActiveRecords ?? false
    );


    const currentPage = useSelector((state: RootState) => state.historical[paginationType].currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical[paginationType].itemsPerPage);

    const handleShowOnlyActives = (value: boolean) => {
        dispatch(setFilterByOnlyActiveRecords({ table: paginationType as FilterTableKey, filterByOnlyActiveRecords: value }));
    };

    const columns: ColumnConfig<ClaimsByPersonResponseDto>[] = [
        {
            header: t("common:form.fields.id"),
            key: "id"
        },
        {
            header: t("claim:newClaimInputNameAndSurname"),
            key: "name"
        },
        {
            header: t("CIP"),
            key: "cip"
        },
        {
            header: t("claim:newClaimInputNameAndDni"),
            key: "dni"
        },
        {
            header: t("claim:EHR"),
            key: "medicalRecordNumber"
        },
        {
            header: t("claim:newClaimInputOacEntryDate"),
            key: "claimEntryDate",
            render: (item) => (
                <>{item.claimEntryDate ? formatDate(item.claimEntryDate) : '-'}</>
            )
        },
        {
            header: t("claim:newClaimInputCreateDate"),
            key: "claimCreationDate",
            render: (item) => (
                <>{item.claimCreationDate ? formatDate(item.claimCreationDate) : '-'}</>
            )
        },
        {
            header: t("claim:newClaimClosedData"),
            key: "claimClosingDate",
            render: (item) => (
                <>{item.claimClosingDate ? formatDate(item.claimClosingDate) : '-'}</>
            )
        },
        {
            header: t("claim:detail.labelStatus"),
            key: "status",
            render: (item) => (
                <>{t(`claim:status.${ClaimStatusEnum[item.status || 0]}`)}</>
            )
        },
        {
            header: t("improvementPlan:demandsModal.columns.alert"),
            key: "hasAlert",
            render: (item) => (
                <TextPill
                    label={item.hasAlerts ? t("common:yes") : t("common:no")}
                    backgroundColor={item.hasAlerts ? "#ff7576" : "white"}
                    textColor={item.hasAlerts ? "white" : "black"}
                    minWidth="70px"
                />
            )
        },
        {
            header: t("claim:newClaimInputComment"),
            key: "comments",
            cellStyle: { verticalAlign: "middle" },
            render: (item) => (
                <span title={(item.comments ?? "")}>
                    {truncateText((item.comments ?? ""), 40) || "-"}
                </span>
            )
        },
        {
            header: t("improvementPlan:demandsModal.columns.alert"),
            key: "type",
            render: (item) => (
                <>{t(`claim:claimPersonType.${ClaimPersonTypeEnum[item.type]}`)}</>
            )
        }
    ];
    
    return (
        <Modal show={show} onHide={onClose} dialogClassName="modal-xxl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{t("claim:claimsForPersonModal.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Form className="my-0">
                        <Form.Check                        
                            className="my-0"
                            type="switch"
                            label={t("claim:claimsForPersonModal.toggleButton")}
                            onChange={() => handleShowOnlyActives(!showOnlyActives)}
                            checked={showOnlyActives}
                        />
                    </Form>
                </Row>
                <AppTable<ClaimsByPersonResponseDto>
                    data={claimsForPerson.items}
                    columns={columns}
                    emptyMessage={t("claim:claimsForPersonModal.noResults")}
                    t={t}
                    hasActions={false}
                    hasPagination={true}
                    currentPage={currentPage}
                    totalPages={claimsForPerson.totalPages}
                    pageSize={itemsPerPage}
                    paginationType={paginationType}
                />
            </Modal.Body>
            
        </Modal>
    );
}

